import cms from './cms';

export interface SendPaymentLinksByIdentifiersRequest {
  identifiers: string[];
  keyCampaign: string;
}

export interface FailedIdentifierItem {
  identifier: string;
  reason: string;
}

export interface SendPaymentLinksByIdentifiersSuccessData {
  failedIdentifiers: FailedIdentifierItem[];
}

/** Success shape: { data: { failedIdentifiers: [...] }, error: null } */
export interface SendPaymentLinksByIdentifiersSuccessResponse {
  data: SendPaymentLinksByIdentifiersSuccessData;
  error: null;
}

/** Error shape returned by the API: { data: null, error: { code, message } } */
export interface PaymentLinksByIdentifiersErrorResponse {
  data: null;
  error: {
    code: string;
    message: string;
  };
}

const DEFAULT_ERROR_MESSAGE = 'Error al enviar. Por favor, inténtalo de nuevo.';

function isPaymentLinksError(
  obj: unknown
): obj is PaymentLinksByIdentifiersErrorResponse {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'error' in obj &&
    (obj as PaymentLinksByIdentifiersErrorResponse).error != null
  );
}

function isPaymentLinksSuccess(
  obj: unknown
): obj is SendPaymentLinksByIdentifiersSuccessResponse {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'data' in obj &&
    'error' in obj &&
    (obj as SendPaymentLinksByIdentifiersSuccessResponse).error === null
  );
}

function getErrorMessage(error: unknown): string {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return DEFAULT_ERROR_MESSAGE;
  }

  const response = (
    error as { response?: { data?: PaymentLinksByIdentifiersErrorResponse } }
  ).response;
  const data = response?.data;
  const apiError = data?.error;

  if (apiError?.message?.trim()) {
    return apiError.message.trim();
  }

  return DEFAULT_ERROR_MESSAGE;
}

export async function sendPaymentLinksByIdentifiers(
  payload: SendPaymentLinksByIdentifiersRequest
): Promise<SendPaymentLinksByIdentifiersSuccessResponse> {
  try {
    const response = await cms.post<
      | SendPaymentLinksByIdentifiersSuccessResponse
      | PaymentLinksByIdentifiersErrorResponse
    >('/v2/payment-links-by-identifiers', payload);

    const data: unknown = response.data;
    if (isPaymentLinksError(data)) {
      const message = data.error.message?.trim() || DEFAULT_ERROR_MESSAGE;
      throw new Error(message);
    }

    if (isPaymentLinksSuccess(data)) {
      return {
        data: {
          failedIdentifiers: data.data.failedIdentifiers ?? [],
        },
        error: null,
      };
    }

    return {
      data: { failedIdentifiers: [] },
      error: null,
    };
  } catch (error: unknown) {
    const apiMessage = getErrorMessage(error);
    if (apiMessage !== DEFAULT_ERROR_MESSAGE) {
      throw new Error(apiMessage);
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(DEFAULT_ERROR_MESSAGE);
  }
}
